import CloseIcon from '@material-ui/icons/ClearOutlined'
import { ToolExecutionState } from 'ducks/chatDrawer'
import {
  AddIcon,
  AlertCircleOutlineIcon,
  Button,
  CheckOutlineIcon,
  IconButton,
  keyframes,
  styled,
  Tooltip,
  Typography,
} from 'opensolar-ui'
import { handleAddSystem } from 'projectSections/sections/design/sideDrawer/autoDesign/V2/helpers'
import { useTranslate } from 'ra-core'
import React from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useCountdownTimeout } from '../../hooks/useCountdownTimeout'
import { validateActionRequirements } from '../../utils/validateActionRequirements'

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-4px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`

interface AutoDesignProgressItemProps {
  toolExecutionState: ToolExecutionState
  messageIndex: number
}

const Container = styled('div')<{ isAutoAccepted?: boolean }>(({ isAutoAccepted }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '10px 12px',
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  borderRadius: '10px',
  marginTop: '12px',
  border: isAutoAccepted ? '2px solid #1751D0' : '1px solid rgba(23, 81, 208, 0.12)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
  position: 'relative',
  overflow: 'hidden',
  animation: `${fadeIn} 0.3s ease-out`,
}))

const StatusRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  position: 'relative',
  zIndex: 1,
})

const Description = styled(Typography)({
  flex: 1,
  fontSize: '13px',
  lineHeight: '18px',
})

const StatusLabel = styled(Typography)({
  fontWeight: 500,
  fontSize: '13px',
  lineHeight: '18px',
  background: 'linear-gradient(90deg, #1751D0 0.01%, #3C91E6 98.75%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
})

const Spinner = styled('div')({
  width: '24px',
  height: '24px',
  border: '3px solid #f3f3f3',
  borderTop: '3px solid #3984d8',
  borderRadius: '50%',
  animation: `${spin} 1s linear infinite`,
})

const StyledButton = styled(Button)({
  minWidth: '36px',
  width: '36px',
  height: '28px',
  padding: '4px',
  border: '2px solid #E5E7EB',
  backgroundColor: '#FFFFFF',
  borderRadius: '6px',
  color: '#6B7280',
  transition: 'color 200ms ease, border-color 200ms ease, box-shadow 200ms ease',
  boxShadow: 'none',
  '&:hover': {
    color: '#1751D0',
    boxShadow: '0 2px 8px rgba(23, 81, 208, 0.12)',
    backgroundColor: '#FFFFFF',
    background: 'none',
    borderColor: '#1751D0',
  },
  '&:disabled': {
    backgroundColor: '#F3F4F6',
    borderColor: '#E5E7EB',
    color: '#9CA3AF',
    boxShadow: 'none',
    cursor: 'not-allowed',
    '&:hover': {
      color: '#9CA3AF',
      boxShadow: 'none',
      backgroundColor: '#F3F4F6',
      borderColor: '#E5E7EB',
    },
  },
  '& svg': {
    width: '16px',
    height: '16px',
  },
})

const CancelButton = styled(IconButton)({
  width: '24px',
  height: '24px',
  padding: '4px',
  color: '#9CA3AF',
  transition: 'color 200ms ease, background-color 200ms ease',
  '&:hover': {
    color: '#6B7280',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  '& svg': {
    width: '16px',
    height: '16px',
  },
})

const IconWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  borderRadius: '6px',
  flexShrink: 0,
})

const ReadyIconWrapper = styled(IconWrapper)({
  background: 'linear-gradient(135deg, rgba(23, 81, 208, 0.1) 0%, rgba(60, 145, 230, 0.1) 100%)',
})

const SuccessIconWrapper = styled(IconWrapper)({
  background: 'linear-gradient(135deg, rgba(1, 102, 38, 0.1) 0%, rgba(1, 144, 48, 0.1) 100%)',
})

const ErrorIconWrapper = styled(IconWrapper)({
  background: 'linear-gradient(135deg, rgba(188, 28, 28, 0.1) 0%, rgba(225, 33, 33, 0.1) 100%)',
})

const CountdownBarContainer = styled('div')({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: '3px',
  borderRadius: '0 0 10px 10px',
  overflow: 'hidden',
  zIndex: 10,
})

const shrinkKeyframes = keyframes`
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
`

const CountdownBarCompleted = styled('div')<{ duration: number }>(({ duration }) => ({
  height: '100%',
  width: '100%',
  backgroundColor: '#016626',
  transformOrigin: 'left',
  animation: `${shrinkKeyframes} ${duration}s linear forwards`,
}))

const CountdownBarError = styled('div')<{ duration: number }>(({ duration }) => ({
  height: '100%',
  width: '100%',
  backgroundColor: '#BC1C1C',
  transformOrigin: 'left',
  animation: `${shrinkKeyframes} ${duration}s linear forwards`,
}))

const AutoAcceptedWrapper: React.FC<{ isAutoAccepted: boolean; children: React.ReactElement }> = ({
  isAutoAccepted,
  children,
}) => {
  const translate = useTranslate()
  if (isAutoAccepted) {
    return (
      <Tooltip title={translate('This action was auto-accepted')} placement="top">
        <span>{children}</span>
      </Tooltip>
    )
  }
  return children
}

function getTemplateId(state: ToolExecutionState): string | null {
  const results = state.data?.autoDesignResult?.template_results || []
  const fromResults =
    results.find((t: any) => t.status === 'success')?.template_id ?? results[0]?.template_id
  if (fromResults != null && fromResults !== '') {
    return String(fromResults)
  }

  if (state.templateId != null && state.templateId !== '') {
    return String(state.templateId)
  }

  return null
}

export const AutoDesignProgressItem: React.FC<AutoDesignProgressItemProps> = ({ toolExecutionState, messageIndex }) => {
  const dispatch = useDispatch()
  const translate = useTranslate()
  const location = useLocation()
  const designData = toolExecutionState.data?.designData || toolExecutionState.designData
  const templateId = getTemplateId(toolExecutionState)
  const isAutoAccepted = toolExecutionState.isAutoAccepted || false

  useCountdownTimeout(toolExecutionState.status, { completed: 5, error: 15 }, dispatch, messageIndex)

  const handleAdd = async () => {
    if (!designData || toolExecutionState.status !== 'ready') return

    const validation = validateActionRequirements('autoDesignEphemeral', location.pathname)
    if (!validation.isValid) {
      console.error('Cannot add system:', validation.message)
      return
    }
    if (!templateId) {
      dispatch({
        type: 'UPDATE_TOOL_EXECUTION_STATE',
        messageIndex,
        toolExecutionState: { ...toolExecutionState, status: 'error', error: { message: 'Template ID not found' } },
      })
      return
    }

    try {
      await handleAddSystem(designData, templateId, dispatch, toolExecutionState.projectId, () => {
        dispatch({
          type: 'UPDATE_TOOL_EXECUTION_STATE',
          messageIndex,
          toolExecutionState: { ...toolExecutionState, status: 'completed' },
        })
      })
    } catch (error) {
      console.error('Error adding auto design system:', error)
      dispatch({
        type: 'UPDATE_TOOL_EXECUTION_STATE',
        messageIndex,
        toolExecutionState: {
          ...toolExecutionState,
          status: 'error',
          error: { message: error instanceof Error ? error.message : 'Failed to add system' },
        },
      })
    }
  }

  const handleCancel = () => {
    // Clear the tool execution state to dismiss the component
    dispatch({
      type: 'UPDATE_TOOL_EXECUTION_STATE',
      messageIndex,
      toolExecutionState: undefined as any,
    })
  }

  // Helper to render countdown bar
  const renderCountdownBar = (
    duration: number,
    backgroundColor: string,
    BarComponent: typeof CountdownBarCompleted | typeof CountdownBarError
  ) => {
    return (
      <CountdownBarContainer style={{ backgroundColor }}>
        <BarComponent duration={duration} />
      </CountdownBarContainer>
    )
  }

  // Render status row content based on status
  const renderStatusContent = () => {
    switch (toolExecutionState.status) {
      case 'running':
        return (
          <>
            <Spinner />
            <Description textVariant="body1" style={{ color: '#666' }}>
              {translate('Running auto-design...')}
            </Description>
            <CancelButton
              size="small"
              onClick={handleCancel}
              aria-label={translate('Cancel')}
              title={translate('Cancel')}
            >
              <CloseIcon />
            </CancelButton>
          </>
        )

      case 'ready': {
        const validation = validateActionRequirements('autoDesignEphemeral', location.pathname)
        let disabledReason: string | null = null
        if (!validation.isValid) disabledReason = validation.message
        else if (!templateId) disabledReason = translate('Template ID not found')
        const isAddDisabled = !!disabledReason
        return (
          <>
            <ReadyIconWrapper>
              <CheckOutlineIcon size={20} color="#1751D0" />
            </ReadyIconWrapper>
            <StatusLabel>{translate('Ready')}</StatusLabel>
            <Description textVariant="body1" style={{ color: '#666' }}>
              {toolExecutionState.description || translate('Auto-design complete')}
            </Description>
            <CancelButton
              size="small"
              onClick={handleCancel}
              aria-label={translate('Cancel')}
              title={translate('Cancel')}
            >
              <CloseIcon />
            </CancelButton>
            <StyledButton
              variant="contained"
              size="small"
              onClick={handleAdd}
              disabled={isAddDisabled}
              aria-label={translate('Add system to design')}
              title={disabledReason ?? translate('Add system to design')}
            >
              <AddIcon />
            </StyledButton>
          </>
        )
      }

      case 'completed':
        return (
          <>
            <SuccessIconWrapper>
              <CheckOutlineIcon size={20} color="#016626" />
            </SuccessIconWrapper>
            <Description
              textVariant="body1"
              style={{
                color: '#016626',
                fontWeight: 500,
              }}
            >
              {translate('System added to design')}
            </Description>
            <CancelButton
              size="small"
              onClick={handleCancel}
              aria-label={translate('Cancel')}
              title={translate('Cancel')}
            >
              <CloseIcon />
            </CancelButton>
          </>
        )

      case 'error': {
        const errorMessage =
          toolExecutionState.error?.message ||
          toolExecutionState.description ||
          toolExecutionState.error?.errorDetails ||
          translate('Auto-design failed')
        return (
          <>
            <ErrorIconWrapper>
              <AlertCircleOutlineIcon size={20} color="#BC1C1C" />
            </ErrorIconWrapper>
            <Description
              textVariant="caption1"
              style={{
                color: '#BC1C1C',
                fontWeight: 500,
              }}
            >
              {errorMessage}
            </Description>
            <CancelButton
              size="small"
              onClick={handleCancel}
              aria-label={translate('Cancel')}
              title={translate('Cancel')}
            >
              <CloseIcon />
            </CancelButton>
          </>
        )
      }

      default:
        return null
    }
  }

  const statusContent = renderStatusContent()
  if (!statusContent) return null

  return (
    <AutoAcceptedWrapper isAutoAccepted={isAutoAccepted}>
      <Container isAutoAccepted={isAutoAccepted}>
        <StatusRow>{statusContent}</StatusRow>
        {toolExecutionState.status === 'completed' &&
          renderCountdownBar(5, 'rgba(1, 102, 38, 0.1)', CountdownBarCompleted)}
        {toolExecutionState.status === 'error' && renderCountdownBar(15, 'rgba(188, 28, 28, 0.1)', CountdownBarError)}
      </Container>
    </AutoAcceptedWrapper>
  )
}
